'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import getCachedHighlighter from './highlighter-light';
import CodeWrapper, {
  Props as CodeWrapperProps,
} from '@/components/CodeSnippet/CodeWrapper';
import { CodeSnippetContext } from './CodeSnippetProvider';
import { generateHTMLFromCode } from '@/components/CodeSnippet/CodeSnippet.helpers';
import type {
  Highlighter,
  ClientLanguage,
  MultipleAnnotations,
  SingleAnnotation,
  HighlightRange,
} from './CodeSnippet.types';
import Spinner from '../Spinner';

type DelegatedProps = Omit<CodeWrapperProps, 'code' | 'html'>;

interface Props extends DelegatedProps {
  onMount?: () => void;
  code?: string;
  lang?: ClientLanguage;
  includeLoadingSpinner?: boolean;
  highlight?: SingleAnnotation | MultipleAnnotations;
  highlightRange?: HighlightRange[];
}

function ClientOnlySnippet({
  onMount,
  code: codeFromProp,
  lang: langFromProp,
  includeLoadingSpinner,
  highlightRange,
  ...delegated
}: Props) {
  const contextValues = React.useContext(CodeSnippetContext);

  const code = codeFromProp || contextValues.code;
  const lang = langFromProp || contextValues.lang;

  const [highlighter, setHighlighter] =
    React.useState<Highlighter>(null);

  React.useEffect(() => {
    let timeoutId: number;

    getCachedHighlighter().then((highlighter) => {
      setHighlighter(highlighter);

      // Give the highlighter a second to work, and then
      if (typeof onMount === 'function') {
        timeoutId = window.setTimeout(() => {
          onMount();
        }, 1000);
      }
    });

    return () => {
      window.clearTimeout(timeoutId);
    };
    // We only want to run this on mount, and so we don't care if `onMount` grows stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!highlighter || !lang) {
    return includeLoadingSpinner ? (
      <SpinnerWrapper>
        <Spinner />
      </SpinnerWrapper>
    ) : null;
  }

  const trimmedCode = code.trim();

  const html = generateHTMLFromCode({
    code: trimmedCode,
    lang,
    highlighter,
    highlightRange,
    ...delegated,
  });

  return (
    <CodeWrapper {...delegated} code={trimmedCode} html={html} />
  );
}

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

export default ClientOnlySnippet;
